import React, { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

interface Snapshot {
  snapshot_date?: string;
  opportunities_discovered?: number;
  comments_generated?: number;
  comments_posted?: number;
  total_conversions?: number;
  total_revenue?: number;
}

interface TrendChartProps {
  data?: Snapshot[];
  timeframe?: string;
}

export default function TrendChart({ data, timeframe = '7d' }: TrendChartProps) {
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];

    return data.map((snapshot) => ({
      date: new Date(snapshot.snapshot_date || '').toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      }),
      Opportunities: snapshot.opportunities_discovered || 0,
      'Comments Generated': snapshot.comments_generated || 0,
      'Comments Posted': snapshot.comments_posted || 0,
      Conversions: snapshot.total_conversions || 0,
      Revenue: snapshot.total_revenue || 0,
    }));
  }, [data]);

  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-gray-500 mb-2">No trend data available yet</p>
          <p className="text-sm text-gray-400">
            Performance trends will appear as data accumulates
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Activity Trends */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Activity Trends</h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey="date"
              stroke="#6b7280"
              style={{ fontSize: '12px' }}
            />
            <YAxis
              stroke="#6b7280"
              style={{ fontSize: '12px' }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '12px',
              }}
            />
            <Legend
              wrapperStyle={{ fontSize: '12px', paddingTop: '20px' }}
            />
            <Line
              type="monotone"
              dataKey="Opportunities"
              stroke="#3b82f6"
              strokeWidth={2}
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
            />
            <Line
              type="monotone"
              dataKey="Comments Generated"
              stroke="#8b5cf6"
              strokeWidth={2}
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
            />
            <Line
              type="monotone"
              dataKey="Comments Posted"
              stroke="#10b981"
              strokeWidth={2}
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Conversion & Revenue Trends */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Conversion & Revenue Trends</h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey="date"
              stroke="#6b7280"
              style={{ fontSize: '12px' }}
            />
            <YAxis
              yAxisId="left"
              stroke="#6b7280"
              style={{ fontSize: '12px' }}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              stroke="#6b7280"
              style={{ fontSize: '12px' }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '12px',
              }}
              formatter={(value: number, name: string) => {
                if (name === 'Revenue') {
                  return [`$${value.toLocaleString()}`, name];
                }
                return [value, name];
              }}
            />
            <Legend
              wrapperStyle={{ fontSize: '12px', paddingTop: '20px' }}
            />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="Conversions"
              stroke="#f59e0b"
              strokeWidth={2}
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="Revenue"
              stroke="#10b981"
              strokeWidth={2}
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 pt-4 border-t border-gray-200">
        <div className="text-center">
          <p className="text-xs text-gray-600 mb-1">Avg Opportunities</p>
          <p className="text-lg font-bold text-blue-600">
            {(chartData.reduce((sum, d) => sum + d.Opportunities, 0) / chartData.length).toFixed(0)}
          </p>
        </div>
        <div className="text-center">
          <p className="text-xs text-gray-600 mb-1">Avg Generated</p>
          <p className="text-lg font-bold text-purple-600">
            {(chartData.reduce((sum, d) => sum + d['Comments Generated'], 0) / chartData.length).toFixed(0)}
          </p>
        </div>
        <div className="text-center">
          <p className="text-xs text-gray-600 mb-1">Avg Posted</p>
          <p className="text-lg font-bold text-green-600">
            {(chartData.reduce((sum, d) => sum + d['Comments Posted'], 0) / chartData.length).toFixed(0)}
          </p>
        </div>
        <div className="text-center">
          <p className="text-xs text-gray-600 mb-1">Avg Conversions</p>
          <p className="text-lg font-bold text-orange-600">
            {(chartData.reduce((sum, d) => sum + d.Conversions, 0) / chartData.length).toFixed(0)}
          </p>
        </div>
        <div className="text-center">
          <p className="text-xs text-gray-600 mb-1">Avg Revenue</p>
          <p className="text-lg font-bold text-emerald-600">
            ${(chartData.reduce((sum, d) => sum + d.Revenue, 0) / chartData.length).toFixed(0)}
          </p>
        </div>
      </div>
    </div>
  );
}
